/**
 * Two wall posters for an incident: one QR code to check in or out, one to sign
 * in or out. Each prints on its own page with only the poster visible.
 */

import { QRCodeSVG } from 'qrcode.react';
import { PrinterIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import {
  QR_ACTIONS,
  QR_POSTER_COPY,
  incidentQrUrl,
  type QrAction,
} from './incidentQr';

const PRINT_CSS = `
@media print {
  @page { size: letter portrait; margin: 0.5in; }
  body * { visibility: hidden !important; }
  .qr-poster-sheet, .qr-poster-sheet * { visibility: visible !important; }
  .qr-poster-sheet { position: absolute; inset: 0; }
  .qr-poster { break-after: page; box-shadow: none !important; border: 0 !important; }
  .qr-poster:last-child { break-after: auto; }
}
`;

const Poster: React.FC<{
  action: QrAction;
  incidentId: string;
  incidentName: string;
}> = ({ action, incidentId, incidentName }) => {
  const url = incidentQrUrl(window.location.origin, incidentId, action);
  const copy = QR_POSTER_COPY[action];
  return (
    <section
      className="qr-poster tw:flex tw:min-h-[9.5in] tw:w-full tw:max-w-[7.5in] tw:flex-col tw:items-center tw:justify-center tw:gap-6 tw:rounded-lg tw:border tw:border-neutral-300 tw:bg-white tw:p-10 tw:text-center tw:text-neutral-900 tw:shadow-sm"
      data-testid={`qr-poster-${action}`}
    >
      <p className="tw:m-0 tw:text-lg tw:font-medium tw:uppercase tw:tracking-wide tw:text-neutral-600">
        {incidentName}
      </p>
      <h1 className="tw:m-0 tw:text-5xl tw:font-bold">{copy.title}</h1>
      <QRCodeSVG
        value={url}
        size={360}
        level="M"
        marginSize={2}
        bgColor="#ffffff"
        fgColor="#000000"
        title={`${copy.title} QR code for ${incidentName}`}
      />
      <p className="tw:m-0 tw:max-w-md tw:text-xl">{copy.instruction}</p>
      <p className="tw:m-0 tw:max-w-md tw:text-sm tw:text-neutral-600">
        Scan with your phone camera and sign in to PRATUS. Only members of this
        incident&apos;s roster can use this code.
      </p>
      <p className="tw:m-0 tw:break-all tw:font-mono tw:text-xs tw:text-neutral-500">
        {url}
      </p>
    </section>
  );
};

export const QrPostersPage: React.FC = () => {
  const incidentId = useActualIncidentId();
  const { data: incident } = useIncident();
  const incidentName = incident?.name ?? 'Incident';

  return (
    <div className="dark tw:flex tw:flex-col tw:gap-4 tw:p-4 tw:text-foreground">
      <style>{PRINT_CSS}</style>
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
        <div className="tw:flex tw:flex-col">
          <h2 className="tw:m-0 tw:text-xl tw:font-semibold">QR posters</h2>
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            Print both and post them where responders arrive. Scanning opens the
            responder&apos;s incident profile with the control ready to tap.
          </p>
        </div>
        <Button
          type="button"
          className="tw:ml-auto"
          onClick={() => window.print()}
        >
          <PrinterIcon />
          Print posters
        </Button>
      </div>
      <div className="qr-poster-sheet tw:flex tw:flex-col tw:items-center tw:gap-6">
        {QR_ACTIONS.map((action) => (
          <Poster
            key={action}
            action={action}
            incidentId={incidentId}
            incidentName={incidentName}
          />
        ))}
      </div>
    </div>
  );
};

export default QrPostersPage;
